import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, Text } from "@pentabd/ui";

import {
  InputDateControllerRow,
  InputTextAreaRow,
  InputTextRow,
  MultiFile,
  ProfileInfoCard,
  SectionTitle,
  SelectControllerRow,
} from "@components";

import { useBaseData } from "@hooks/useBaseData";
// import { useEmployeeServiceData } from "@hooks/useEmployeeServiceData";
import { useLanguage } from "@hooks/useLanguage";

import { getConvertedUnitData } from "@helpers/baseDataConvert";
import { getEmployeeServiceDataConvert } from "@helpers/employeeServiceDataConvert";
import { ticketDropdownDataConverter } from "@helpers/tickets/ticketDropdownDataConverter";

import { BASE_DATA } from "@constants/base-data";
// import { EMPLOYEE_SERVICE_DATA } from "@constants/employee-service-data";
import { statusENUM } from "@containers/Tickets/statusData";

import {
  TICKET_DETAILS,
  TicketFormDataType,
} from "@validations/Tickets/ticketFormValidation";
import { STATUS } from "@constants/status.eTicket";
import { useUnitWiseEmployee } from "@hooks/tickets/e-tickets/useUnitWiseEmployee";
import { useUnitsForAllTickets } from "@hooks/tickets/e-tickets/useUnitsForAllTickets";
import { useEffect } from "react";

const { ON_GOING } = STATUS;

export const TicketDetailsBody = ({
  data,
  ticketDropdown,
  redirectToListPage,
  isAssignDisabled,
}: {
  data: any;
  ticketDropdown: any;
  redirectToListPage: () => void;
  isAssignDisabled: boolean;
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const { ticketTypes, categories, priorities } = ticketDropdown;

  const { data: wings } = useBaseData(BASE_DATA.WINGS);
  const {
    register,
    setValue,
    control,
    formState: { errors },
    watch,
  } = useFormContext<TicketFormDataType>();
  // const { data: commonInfoResponse } = useEmployeeServiceData({
  //   dataType: EMPLOYEE_SERVICE_DATA.COMMON_INFO,
  //   unitIds: "30,31,32,33", //Information and Technology Cell
  // });

  // const commonInfo = commonInfoResponse?.commonInfo || [];
  const { employees, handleQuery: handleEmployeeQuery } = useUnitWiseEmployee();
  const { units: assignedToUnit } = useUnitsForAllTickets();

  const unitId = watch(TICKET_DETAILS.UNIT_ID);

  useEffect(() => {
    if (unitId) {
      handleEmployeeQuery("unitId", unitId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId]);

  return (
    <div>
      {/* ticket information  */}
      <SectionTitle label="TICKETS.DETAILS.FORM.TICKET_INFO.LABEL" size="lg" />

      <div className="d-flex flex-column gap-8">
        <SelectControllerRow
          label="TICKETS.DETAILS.FORM.TICKET_INFO.TYPE"
          name={TICKET_DETAILS.TYPE}
          register={{ ...register(TICKET_DETAILS.TYPE) }}
          options={ticketDropdownDataConverter(ticketTypes)}
          setValue={setValue}
          control={control}
          error={errors}
          disabled
        />
        <SelectControllerRow
          label="TICKETS.DETAILS.FORM.TICKET_INFO.CATEGORY"
          name={TICKET_DETAILS.CATEGORY}
          register={{ ...register(TICKET_DETAILS.CATEGORY) }}
          options={ticketDropdownDataConverter(categories)}
          setValue={setValue}
          control={control}
          error={errors}
          disabled
        />
        <InputTextRow
          register={{ ...register(TICKET_DETAILS.SUBJECT) }}
          label="TICKETS.DETAILS.FORM.TICKET_INFO.SUBJECT"
          error={errors}
          disabled
        />
        <InputTextRow
          register={{ ...register(TICKET_DETAILS.ISSUE_NAME) }}
          label="TICKETS.DETAILS.FORM.TICKET_INFO.ISSUE_NAME"
          error={errors}
          disabled
        />
        <InputTextAreaRow
          register={{ ...register(TICKET_DETAILS.ISSUE_DETAILS) }}
          label="TICKETS.DETAILS.FORM.TICKET_INFO.ISSUE_DETAILS"
          error={errors}
          disabled
        />
        <div className="row">
          <div className="col-lg-3">
            <Text weight="semibold" color="title" size="sm">
              {t("TICKETS.DETAILS.FORM.TICKET_INFO.CLIENT.LABEL")}
            </Text>
          </div>
          <ProfileInfoCard employee={data?.ticketCreatedBy} />
        </div>
        <SelectControllerRow
          label="TICKETS.DETAILS.FORM.TICKET_INFO.UNIT"
          name={TICKET_DETAILS.UNIT}
          register={{ ...register(TICKET_DETAILS.UNIT) }}
          options={getConvertedUnitData(wings, language)}
          setValue={setValue}
          control={control}
          error={errors}
          disabled
        />
        <InputDateControllerRow
          label="TICKETS.DETAILS.FORM.TICKET_INFO.CREATED"
          control={control}
          register={{ ...register(TICKET_DETAILS.CREATED) }}
          errors={errors}
          name={TICKET_DETAILS.CREATED}
          disabled
        />
        <SelectControllerRow
          label="TICKETS.DETAILS.FORM.TICKET_INFO.ASSIGNED_TO_UNIT"
          name={TICKET_DETAILS.UNIT_ID}
          register={{ ...register(TICKET_DETAILS.UNIT_ID) }}
          options={getConvertedUnitData(assignedToUnit, language)}
          setValue={setValue}
          control={control}
          required
          disabled={isAssignDisabled}
        />
        <SelectControllerRow
          label="TICKETS.DETAILS.FORM.TICKET_INFO.ASSIGNED_TO"
          name={TICKET_DETAILS.ASSIGNED_TO}
          register={{ ...register(TICKET_DETAILS.ASSIGNED_TO) }}
          options={getEmployeeServiceDataConvert(employees, language)}
          setValue={setValue}
          selectAction={() => setValue("status", ON_GOING)}
          control={control}
          error={errors}
          required
          disabled={isAssignDisabled || !unitId}
        />
        <SelectControllerRow
          label="TICKETS.DETAILS.FORM.TICKET_INFO.PRIORITY"
          name={TICKET_DETAILS.PRIORITY}
          register={{ ...register(TICKET_DETAILS.PRIORITY) }}
          options={ticketDropdownDataConverter(priorities)}
          setValue={setValue}
          control={control}
          error={errors}
          disabled
        />
        <SelectControllerRow
          label="TICKETS.DETAILS.FORM.TICKET_INFO.STATUS"
          name={TICKET_DETAILS.STATUS}
          register={{ ...register(TICKET_DETAILS.STATUS) }}
          options={statusENUM}
          setValue={setValue}
          control={control}
          error={errors}
          required
        />
      </div>

      {/* document upload  */}
      <SectionTitle
        label="TICKETS.DETAILS.FORM.SUPPORTING_DOCUMENTS.LABEL"
        size="lg"
      />

      <div className="row">
        <div className="col-lg-3">
          <Text weight="semibold" color="title" size="sm" component="p">
            {t("TICKETS.DETAILS.FORM.SUPPORTING_DOCUMENTS.DOCUMENTS.LABEL")}
          </Text>
        </div>
        <div className="col-lg-7">
          <MultiFile
            id={TICKET_DETAILS.DOCUMENTS}
            control={control}
            setValue={setValue}
            register={register}
            errors={errors}
            disabled
          />
        </div>
      </div>

      <div className="d-flex justify-content-end gap-8 border-top pt-10 mt-10">
        <Button
          fill="outline"
          type="light"
          size="sm"
          onClick={redirectToListPage}
        >
          {t("TICKETS.DETAILS.FORM.CANCEL")}
        </Button>

        <Button fill="fill" type="primary" size="sm" htmlType="submit">
          {t("TICKETS.DETAILS.FORM.UPDATE")}
        </Button>
      </div>
    </div>
  );
};
